import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { PageContent, PageHeader } from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Play, RefreshCw, Search, Square, Trash2, Activity } from "lucide-react";
import { toast } from "sonner";

const blankSource = {
  name: "",
  source_type: "hls",
  url: "",
  auto_restart: true,
  active: true,
  timeout_seconds: 20,
  max_retries: 0,
};

function fmt(value, fallback = "-") {
  return value === undefined || value === null || value === "" ? fallback : value;
}

function statusClass(source) {
  if (source.runtime?.running) return "text-emerald-300";
  if (["error", "offline"].includes(source.status)) return "text-red-300";
  if (source.status === "reconnecting") return "text-amber-300";
  return "text-slate-400";
}

export default function StreamingManagerPage() {
  const [sources, setSources] = useState([]);
  const [form, setForm] = useState(blankSource);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    const { data } = await api.get("/streams");
    setSources(data || []);
  };

  useEffect(() => {
    load().catch(() => toast.error("Errore caricamento Streaming Manager"));
    const timer = setInterval(() => load().catch(() => {}), 10000);
    return () => clearInterval(timer);
  }, []);

  const filtered = useMemo(() => {
    const text = query.trim().toLowerCase();
    if (!text) return sources;
    return sources.filter((source) =>
      [source.name, source.url, source.source_type, source.status]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(text))
    );
  }, [sources, query]);

  const totals = useMemo(() => ({
    total: sources.length,
    running: sources.filter((source) => source.runtime?.running).length,
    errors: sources.filter((source) => ["error", "offline"].includes(source.status)).length,
    processes: sources.filter((source) => source.runtime?.pid).length,
  }), [sources]);

  const save = async () => {
    if (!form.name || !form.url) {
      toast.error("Nome e URL sorgente sono obbligatori");
      return;
    }
    setLoading(true);
    try {
      await api.post("/streams", form);
      setForm(blankSource);
      await load();
      toast.success("Sorgente creata");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Creazione non riuscita");
    } finally {
      setLoading(false);
    }
  };

  const action = async (id, name) => {
    setLoading(true);
    try {
      await api.post(`/streams/${id}/${name}`);
      await load();
      toast.success(`Comando ${name} inviato`);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Comando non riuscito");
    } finally {
      setLoading(false);
    }
  };

  const check = async (id) => {
    setLoading(true);
    try {
      await api.post(`/streams/${id}/check`);
      await load();
      toast.success("Controllo sorgente completato");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Controllo non riuscito");
    } finally {
      setLoading(false);
    }
  };

  const remove = async (id) => {
    setLoading(true);
    try {
      await api.delete(`/streams/${id}`);
      if (selected?.id === id) setSelected(null);
      await load();
      toast.success("Sorgente eliminata");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Eliminazione sorgente non riuscita");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="streaming-manager-page">
      <PageHeader
        title="Streaming Manager"
        subtitle="Centro operativo backend per sorgenti, FFmpeg, diagnostica e riavvii automatici."
      />
      <PageContent>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-slate-900 border border-slate-800 rounded-md p-4">
            <div className="text-xs text-slate-500 font-mono">Sorgenti</div>
            <div className="text-2xl font-black text-white mt-1">{totals.total}</div>
          </div>
          <div className="bg-slate-900 border border-slate-800 rounded-md p-4">
            <div className="text-xs text-slate-500 font-mono">Live backend</div>
            <div className="text-2xl font-black text-emerald-300 mt-1">{totals.running}</div>
          </div>
          <div className="bg-slate-900 border border-slate-800 rounded-md p-4">
            <div className="text-xs text-slate-500 font-mono">Errori</div>
            <div className="text-2xl font-black text-red-300 mt-1">{totals.errors}</div>
          </div>
          <div className="bg-slate-900 border border-slate-800 rounded-md p-4">
            <div className="text-xs text-slate-500 font-mono">Processi FFmpeg</div>
            <div className="text-2xl font-black text-sky-300 mt-1">{totals.processes}</div>
          </div>
        </div>

        <div className="bg-slate-900 border border-slate-800 rounded-md p-4 grid grid-cols-1 lg:grid-cols-6 gap-3">
          <div>
            <Label>Nome</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div>
            <Label>Tipo</Label>
            <select
              className="w-full h-10 rounded-md bg-slate-950 border border-slate-800 px-3 text-sm text-white"
              value={form.source_type}
              onChange={(e) => setForm({ ...form, source_type: e.target.value })}
            >
              <option value="hls">HLS</option>
              <option value="mpegts">MPEGTS</option>
              <option value="rtmp">RTMP</option>
              <option value="srt">SRT</option>
              <option value="udp">UDP</option>
            </select>
          </div>
          <div className="lg:col-span-3">
            <Label>URL sorgente</Label>
            <Input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} />
          </div>
          <div className="flex items-end">
            <Button className="w-full" onClick={save} disabled={loading}>Crea sorgente</Button>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Search size={16} className="text-slate-500" />
          <Input
            placeholder="Cerca sorgente, URL, stato..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="max-w-xl"
          />
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          <div className="xl:col-span-2 space-y-3">
            {filtered.map((source) => (
              <div key={source.id} className="bg-slate-900 border border-slate-800 rounded-md p-4">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <Activity size={15} className={statusClass(source)} />
                      <div className="font-bold text-white truncate">{source.name}</div>
                      <span className="text-[10px] uppercase font-mono text-slate-500">{source.source_type}</span>
                    </div>
                    <div className="text-xs text-slate-500 truncate mt-1">{source.url}</div>
                    <div className="text-xs font-mono mt-2 text-slate-400">
                      Stato: <span className={statusClass(source)}>{source.runtime?.running ? "running" : source.status}</span>
                      {" "}· PID: {fmt(source.runtime?.pid)} · Uptime: {source.runtime?.uptime_seconds || 0}s · Restart: {source.restart_count || 0}
                    </div>
                    <div className="text-xs font-mono mt-1 text-slate-500">
                      FPS {fmt(source.fps)} · Bitrate {fmt(source.bitrate)} · {fmt(source.resolution)} · V:{fmt(source.video_codec)} · A:{fmt(source.audio_codec)}
                    </div>
                    {(source.last_error_label || source.last_error) && (
                      <div className="text-xs text-red-300 mt-1 truncate max-w-2xl">
                        {source.last_error_label || "Errore"}{source.failing_input_url ? ` · URL: ${source.failing_input_url}` : ""}{source.last_error ? ` · ${source.last_error}` : ""}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" onClick={() => action(source.id, "start")} disabled={loading}><Play size={14} /> Avvia</Button>
                    <Button size="sm" variant="secondary" onClick={() => action(source.id, "restart")} disabled={loading}><RefreshCw size={14} /> Restart</Button>
                    <Button size="sm" variant="outline" onClick={() => action(source.id, "stop")} disabled={loading}><Square size={14} /> Stop</Button>
                    <Button size="sm" variant="outline" onClick={() => check(source.id)} disabled={loading}>Test sorgente</Button>
                    <Button size="sm" variant="ghost" onClick={() => setSelected(source)}>Log</Button>
                    <Button size="sm" variant="ghost" className="text-red-400" onClick={() => remove(source.id)} disabled={loading}><Trash2 size={14} /></Button>
                  </div>
                </div>
              </div>
            ))}
            {filtered.length === 0 && (
              <div className="bg-slate-900 border border-slate-800 rounded-md p-8 text-center text-slate-500">
                Nessuna sorgente gestita.
              </div>
            )}
          </div>
          <div className="bg-slate-900 border border-slate-800 rounded-md p-4 min-h-80">
            <div className="font-bold text-white">Log sorgente</div>
            <div className="text-xs text-slate-500 mt-1 truncate">{selected?.name || "Seleziona una sorgente"}</div>
            <pre className="mt-3 h-[32rem] overflow-auto rounded-md bg-slate-950 p-3 text-xs text-slate-400">
              {selected ? (selected.runtime?.logs || []).join("\n") || "Nessun log disponibile." : "Nessuna sorgente selezionata."}
            </pre>
          </div>
        </div>
      </PageContent>
    </div>
  );
}
